/**
 * Profile page: the user's sport profiles (cricket today). One card per sport
 * the app supports; editing uses the same schema-driven fields as the
 * tournament registration form.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '../../config/axios';
import SportProfileFields, { useSportSchema } from './SportProfileFields';

const inputCls = 'w-full border-2 border-gray-100 focus:border-[#2B3A67] rounded-xl px-3 py-2.5 text-sm outline-none';

function Field({ label, required, children }) {
  return (
    <div>
      <p className="text-xs font-bold text-gray-500 mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</p>
      {children}
    </div>
  );
}

function SportCard({ sport, saved, onSaved }) {
  const spec = useSportSchema(sport);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(saved || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(saved || {}); }, [saved]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put(`/auth/sport-profiles/${sport}`, { data: value });
      onSaved(res.data.data);
      setEditing(false);
      toast.success(`${spec?.label || 'Sport'} profile saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!spec) return null;
  const filled = spec.fields.filter((f) => saved?.[f.key]);
  return (
    <div className="rounded-xl border border-gray-100 p-4" data-testid={`sport-card-${sport}`}>
      <div className="flex items-center justify-between">
        <p className="font-bold">{spec.label}</p>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className="text-sm font-bold text-[#E54562]"
            data-testid={`edit-sport-${sport}`}>
            {filled.length ? 'Edit' : 'Add'}
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-3 space-y-4">
          <SportProfileFields sport={sport} value={value} onChange={setValue} inputCls={inputCls} Field={Field} />
          <div className="flex gap-2">
            <button type="button" onClick={() => { setValue(saved || {}); setEditing(false); }}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Cancel</button>
            <button type="button" onClick={save} disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-[#2B3A67] text-white text-sm font-bold disabled:opacity-50"
              data-testid={`save-sport-${sport}`}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : filled.length ? (
        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          {filled.map((f) => (
            <div key={f.key} className="min-w-0">
              <p className="text-xs text-gray-400">{f.label}</p>
              {f.kind === 'url'
                ? <a href={saved[f.key]} target="_blank" rel="noreferrer" className="font-medium text-[#2B3A67] underline truncate block">Open link</a>
                : <p className="font-medium">{saved[f.key]}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500 mt-1">Add your playing details once — tournament registrations are pre-filled from here.</p>
      )}
    </div>
  );
}

export default function SportProfilesSection() {
  const [schema, setSchema] = useState(null);
  const [profiles, setProfiles] = useState({});

  useEffect(() => {
    axiosInstance.get('/auth/sport-profiles/schema').then((r) => setSchema(r.data || [])).catch(() => setSchema([]));
    axiosInstance.get('/auth/sport-profiles').then((r) => setProfiles(r.data || {})).catch(() => {});
  }, []);

  if (!schema?.length) return null;
  return (
    <section className="bg-white rounded-2xl p-5 shadow-md border border-gray-100" data-testid="profile-sports">
      <h3 className="font-extrabold">Sports Profiles</h3>
      <p className="text-xs text-gray-400 mb-4">Your playing details per sport, used to pre-fill tournament registrations.</p>
      <div className="space-y-3">
        {schema.map((s) => (
          <SportCard key={s.sport} sport={s.sport} saved={profiles[s.sport]?.data}
            onSaved={(data) => setProfiles((p) => ({ ...p, [s.sport]: { data } }))} />
        ))}
      </div>
    </section>
  );
}
